import { createReactContext } from "#utils/reactContext";

/**
 * Context data for the Payments components (list view and slideout drawer)
 * This context provides configuration values that determine how the components
 * should behave based on the entry point (GPx vs Canopy) and user permissions.
 */
export type PaymentsContextData = {
  /**
   * Indicates if the user is in the GPx entry point
   * @default false
   */
  isGPX: boolean;

  /**
   * Indicates if the current user has permission to approve payments
   * @default false
   */
  hasApprovePaymentPermission: boolean;

  /**
   * The ID of the current user (required - no default)
   */
  currentUserId: number;

  /**
   * Enables M3 capital activity (capital call / transaction batch) support
   * in PaymentDetailsDisplay. When false, only distribution batches are
   * handled by the Level 2 slideout.
   * @default false
   */
  isM3Enabled?: boolean;
};

/**
 * Initial state for the Payments context with default values
 * Note: currentUserId must be provided by the consumer
 */
export const initialPaymentsContextState: Omit<PaymentsContextData, "currentUserId"> = {
  isGPX: false,
  hasApprovePaymentPermission: false,
};

/**
 * Hook to access the Payments context data
 * Must be used within a PaymentsContextProvider
 */
const [usePaymentsContext, PaymentsContextProvider] = createReactContext<PaymentsContextData>();

export { usePaymentsContext, PaymentsContextProvider };
